import React from 'react';
import {
  Bar,
  Cell,
  ComposedChart,
  Legend,
  XAxis,
  YAxis,
  Tooltip,
  Line,
  ResponsiveContainer,
  ReferenceLine,
  CartesianGrid,
  Label,
  Area,
} from 'recharts';
import {
  startOfDay,
  endOfDay,
  format,
  subYears,
  isBefore,
  endOfMonth,
  isAfter,
  isSameDay,
  isWithinInterval,
  subMonths,
  subWeeks,
  subDays,
  differenceInCalendarDays,
  addYears,
} from 'date-fns';
import { useTranslation } from 'react-i18next';
import styles from './styles/ReportingGraph.module.scss';

import Button from '../../Common/Button';
import Loading from '../../Common/Loading';
import Select from '../../Common/Select';
import Switch from '../../Common/Switch';

import { ReportingTable } from './ReportingTable';
import { ReportingHeader } from './ReportingHeader';

import { OmaLummeApp, ConsumptionApp } from '../../../omaLummeContext';

// Actions.
import {
  fetchProfileCost,
  fetchMeteringPointGroupConsumption,
  createMeteringPointGroupConsumptionReport,
  fetchConsumptionRange,
} from '../../../actions';
// Types.
import { ConsumptionItem, MeteringPointOption } from '../../../types/consumptionRates';
import { ReportingGraphDatapoint, ReportingGraphProps } from '../../../types/graphs';
// Utils.
import { setPriceFormat, roundNumber, numberToString } from '../../../utils/billing';
import { CustomTooltip, ReferenceLabel, generateDataPoints, generateMeteringPointOptions, graphDateStyle } from '../../../utils/consumption';
import { gaEventIfAllowed } from '../../../utils/googleAnalytics';
import { useWindowSize } from '../../Layout/Header';

export const pastEstimateDays = 2;

export const total = (arr: ReportingGraphDatapoint[], key: 'consumption' | 'costWithVat' | 'costWithoutVat' | 'consumptionEstimate') => {
  return arr.reduce((acc: number, obj: ReportingGraphDatapoint) => {
    return acc + ((obj && obj[key]) ?? 0);
  }, 0);
};

export const avg = (arr: ReportingGraphDatapoint[], key: 'consumption' | 'consumptionWithEstimates' | 'costWithVat' | 'costWithoutVat') => {
  return (
    arr.reduce((acc: number, obj: ReportingGraphDatapoint) => {
      if (key === 'consumptionWithEstimates') {
        return acc + ((obj && obj['consumption']) ?? 0) + ((obj && obj['consumptionEstimate']) ?? 0);
      }
      return acc + ((obj && obj[key]) ?? 0);
    }, 0) / arr.length
  );
};

type GraphControls = {
  activeFilter: string;
  startDate: Date | undefined;
  endDate: Date | undefined;
  meteringPointGroup?: string;
};

const ReportingGraph: React.FunctionComponent<ReportingGraphProps> = (props: ReportingGraphProps) => {
  const { t } = useTranslation();
  const dispatch = ConsumptionApp.useDispatch();
  const dispatchGlobal = OmaLummeApp.useDispatch();
  const { previousConsumption, lastYearConsumption } = ConsumptionApp.useState();
  const [fetchingPreviousConsumption, setFetchingPreviousConsumption] = React.useState<boolean>(false);
  const [fetchingLastYearConsumption, setFetchingLastYearConsumption] = React.useState<boolean>(false);
  const { basicData, users, selectedMeteringPoint, selectedMeteringPointContract } = OmaLummeApp.useState();
  const { activeUser } = users;
  const allContracts = OmaLummeApp.useState().contracts;

  const [profileCost, setProfileCost] = React.useState<number>();
  const [showDownloadModal, setShowDownloadModal] = React.useState<boolean>(false);
  const [emptyData, setEmptyData] = React.useState<boolean>(true);

  const escFunction = React.useCallback((event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      setShowDownloadModal(false);
    }
  }, []);

  React.useEffect(() => {
    document.addEventListener('keydown', escFunction, false);

    return () => {
      document.removeEventListener('keydown', escFunction, false);
    };
  }, [escFunction]);

  function useOutsideAlerter(
    ref: React.RefObject<any>,
    open: {
      showDownloadModal: boolean;
      setShowDownloadModal: (value: React.SetStateAction<boolean>) => void;
    },
  ) {
    React.useEffect(() => {
      /**
       * Alert if clicked on outside of element
       */
      function handleClickOutside(event: MouseEvent) {
        if (ref.current && !ref.current.contains(event.target)) {
          open.setShowDownloadModal(false);
        }
      }

      // Bind the event listener
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        // Unbind the event listener on clean up
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [ref, open]);
  }

  const downloadRef = React.useRef<any>(null);
  useOutsideAlerter(downloadRef, { showDownloadModal, setShowDownloadModal });

  const { activeFilter, dataLoading, startDate, endDate, consumption, graphData, eliqForecast, weather, nordPoolData } = ConsumptionApp.useState();

  const {
    latestDay,
    latestDayFetched,
    contractIsNoPrice,
    consumptionAsMoney,
    dayConsumptionAs15min,
    showComparisonData,
    totalConsumption,
    consumptionEstimate,
  } = ConsumptionApp.useState();

  const meteringpointContracts = basicData?.filter((mp) => mp.GSRN === selectedMeteringPoint?.meteringPointId);

  const basicContract = meteringpointContracts && meteringpointContracts[0];

  if (basicContract && meteringpointContracts && meteringpointContracts?.length > 1) {
    const firstContract = meteringpointContracts.sort((a, b) => (isAfter(new Date(a.contractStartDate), new Date(b.contractStartDate)) ? 1 : -1))[0];

    basicContract.contractStartDate = firstContract.contractStartDate;
  }

  const customerType = activeUser.customerType ?? 'private';
  const { type, meteringPointGroup } = props;
  const [downloadingHourlyCsv, setDownloadingHourlyCsv] = React.useState<boolean>(false);
  const [downloadingMeteringPointGroupConsumptionReport, setDownloadingMeteringPointGroupConsumptionReport] = React.useState<boolean>(false);

  const [prevControls, setPrevControls] = React.useState<GraphControls>({ activeFilter, startDate, endDate });

  const meteringPointType = basicContract && basicContract.meteringpointType ? (basicContract.meteringpointType.toLowerCase() ?? '') : '';
  const productionContract = meteringPointType === 'production';

  const startDateInPast = basicContract ? isAfter(new Date(basicContract.contractStartDate), new Date()) : false;

  const optionString = selectedMeteringPoint
    ? `${selectedMeteringPoint.meteringPointId}-${selectedMeteringPoint.contractNo}-${selectedMeteringPoint.contractCode}`
    : ``;

  const [activeOptionMP, setActiveOptionMP] = React.useState<string>(optionString);
  const [activeOptionLine, setActiveOptionLine] = React.useState<string>('temperature');
  const [graphType, setGraphType] = React.useState<string>('bar');

  const handleBarClick = (bar: ReportingGraphDatapoint): void => {
    if (bar.resolution === 'year' && bar.time) {
      dispatch(['changeActiveFilter', 'month']);
      dispatch(['changeStartDate', new Date(bar.time)]);
      dispatch(['changeEndDate', endOfMonth(new Date(bar.time))]);
    }
    if ((bar.resolution === 'month' || bar.resolution === 'week') && bar.time) {
      dispatch(['changeActiveFilter', 'day']);
      dispatch(['changeStartDate', new Date(bar.time)]);
      dispatch(['changeEndDate', endOfDay(new Date(bar.time))]);
    }
  };

  React.useEffect(() => {
    dispatch(['changeDataLoading', true]);
    dispatch(['changeConsumption', undefined]);
    dispatch(['changeForecast', undefined]);
    dispatch(['changeWeather', undefined]);
    dispatch(['changeSpot', undefined]);
    dispatch(['changeGraphData', undefined]);
    dispatch(['changeWeightedAvg', undefined]);
    dispatch(['changeConsumptionAvg', undefined]);
    setEmptyData(true);

    dispatch(['changePreviousConsumption', 0]);
    dispatch(['changeLastYearConsumption', 0]);
    dispatch(['changeConsumptionEstimate', 0]);
  }, [activeUser.userId, basicContract, startDate, endDate]);

  React.useEffect(() => {
    if (!graphData) {
      setEmptyData(true);
    }
  }, [graphData]);

  React.useEffect(() => {
    if (selectedMeteringPoint) {
      setActiveOptionMP(`${selectedMeteringPoint.meteringPointId}-${selectedMeteringPoint.contractNo}-${selectedMeteringPoint.contractCode}`);
    }
  }, [selectedMeteringPoint]);

  React.useEffect(() => {
    if (
      selectedMeteringPointContract === undefined ||
      (selectedMeteringPointContract &&
        selectedMeteringPointContract.deliveries &&
        startDate &&
        endDate &&
        selectedMeteringPointContract.deliveries
          .filter((d) => isBefore(startDate, new Date(d.toDate)) && isAfter(endDate, new Date(d.fromDate)))
          .filter(
            (d) =>
              d.externalName.toLowerCase().includes('synergiasähkö') ||
              d.elementName.toLowerCase() === 'yrityssähkö' ||
              d.externalName.toLowerCase().includes('salkku'),
          ).length > 0)
    ) {
      dispatch(['changeContractIsNoPrice', true]);
    } else {
      dispatch(['changeContractIsNoPrice', false]);
    }
  }, [customerType, selectedMeteringPointContract]);

  const handleMeteringPointChange = (option: string): void => {
    dispatchGlobal([
      'changeSelectedMeteringPoint',
      {
        customerId: parseInt(basicContract?.customerNo ?? '0'),
        contractCode: option.split('-')[2],
        contractNo: parseInt(option.split('-')[1]),
        meteringPointId: option.split('-')[0],
      },
    ]);

    if (basicContract) {
      if (
        startDate &&
        (startDate < new Date(basicContract.contractStartDate) ||
          (basicContract.contractEndDate && startDate > new Date(basicContract.contractEndDate)))
      ) {
        dispatch(['changeStartDate', new Date(basicContract.contractStartDate)]);
      }
      if (
        endDate &&
        ((basicContract.contractEndDate && endDate > new Date(basicContract.contractEndDate)) || endDate < new Date(basicContract.contractStartDate))
      ) {
        dispatch(['changeEndDate', new Date(basicContract.contractEndDate)]);
      }
      dispatch(['changeLatestDayFetched', false]);
    }
  };

  React.useEffect(() => {
    if (activeOptionMP) {
      handleMeteringPointChange(activeOptionMP);
    }
  }, [activeOptionMP, allContracts]);

  React.useEffect(() => {
    async function fetchGroupConsumptionData(): Promise<void> {
      dispatch(['changeDataLoading', true]);

      let viewPeriod = 'Month';
      if (activeFilter === 'year') {
        viewPeriod = 'Year';
      }
      if (activeFilter === 'day') {
        viewPeriod = 'Day';
      }

      if (meteringPointGroup && startDate && endDate) {
        const rates = await fetchMeteringPointGroupConsumption(
          activeUser.userId,
          meteringPointGroup,
          startOfDay(startDate)?.toISOString(),
          endOfDay(endDate)?.toISOString(),
          viewPeriod,
        );
        if (rates) {
          const newData = generateDataPoints(rates.items, activeFilter, t, customerType, contractIsNoPrice, meteringPointType, eliqForecast, weather);
          dispatch(['changeDataLoading', false]);
          dispatch(['changeConsumption', rates]);
          dispatch(['changeGraphData', newData]);

          if (newData) {
            setEmptyData(false);
          }

          if (
            rates &&
            (!latestDayFetched || isAfter(new Date(rates.items.filter((day) => day.sum > 0).pop()?.startTime || new Date()), latestDay || new Date()))
          ) {
            const latestConsumptionDay: ConsumptionItem | undefined = rates.items.filter((day) => day.sum > 0).pop();
            if (latestConsumptionDay) {
              dispatch(['changeLatestDay', new Date(latestConsumptionDay.startTime)]);
              dispatch(['changeLatestDayFetched', true]);
            }
          }
        }
      }
    }
    async function fetchComparison(): Promise<void> {
      if (selectedMeteringPoint && startDate && endDate && activeFilter !== 'day') {
        let start = startOfDay(startDate).toISOString();
        let end = endOfDay(endDate).toISOString();

        if (activeFilter === 'year') {
          start = startOfDay(subYears(startDate, 1)).toISOString();
          end = endOfDay(subYears(endDate, 1)).toISOString();
        }
        if (activeFilter === 'month') {
          start = startOfDay(subMonths(startDate, 1)).toISOString();
          end = endOfDay(subMonths(endDate, 1)).toISOString();
        }
        if (activeFilter === 'week') {
          start = startOfDay(subWeeks(startDate, 1)).toISOString();
          end = endOfDay(subWeeks(endDate, 1)).toISOString();
        }
        if (graphDateStyle(startDate, endDate, activeFilter) === 'custom') {
          start = startOfDay(subDays(startDate, differenceInCalendarDays(endDate, startDate) + 1)).toISOString();
          end = endOfDay(subDays(endDate, 1)).toISOString();
        }

        const endIsEndOfMonth = isSameDay(endDate, endOfMonth(endDate));

        if (endIsEndOfMonth) {
          end = endOfMonth(new Date(end)).toISOString();
        }

        if (!fetchingPreviousConsumption) {
          setFetchingPreviousConsumption(true);

          const prevRates = await fetchConsumptionRange(
            selectedMeteringPoint.customerId,
            activeUser.userId,
            selectedMeteringPoint.meteringPointId,
            'Month',
            start,
            end,
          );
          if (prevRates) {
            setFetchingPreviousConsumption(false);
            const prevData = generateDataPoints(prevRates.items, activeFilter, t, customerType, contractIsNoPrice, meteringPointType);
            dispatch(['changePreviousConsumption', total(prevData, 'consumption')]);
          }
        }
        if (!fetchingLastYearConsumption) {
          setFetchingLastYearConsumption(true);
          const lastYearRates = await fetchConsumptionRange(
            selectedMeteringPoint.customerId,
            activeUser.userId,
            selectedMeteringPoint.meteringPointId,
            'Month',
            startOfDay(subYears(startDate, 1))?.toISOString(),
            endOfDay(subYears(endDate, 1))?.toISOString(),
          );
          if (lastYearRates) {
            setFetchingLastYearConsumption(false);
            const lastYearData = generateDataPoints(lastYearRates.items, activeFilter, t, customerType, contractIsNoPrice, meteringPointType);
            dispatch(['changeLastYearConsumption', total(lastYearData, 'consumption')]);
          }
        }
      }
    }
    async function fetchCostData(): Promise<void> {
      const consumptionImpactDelivery =
        selectedMeteringPointContract?.deliveries?.find((d) => d.acticeToday === 1 && d.elementName.toLowerCase().includes('kulutusvaikutus'));

      const start =
        consumptionImpactDelivery && startDate && isWithinInterval(consumptionImpactDelivery.tariffFromDate, { start: startDate, end: new Date() })
          ? new Date(consumptionImpactDelivery.tariffFromDate)
          : startDate;

      if (
        selectedMeteringPointContract &&
        consumptionImpactDelivery &&
        start &&
        startDate &&
        endDate &&
        new Date(consumptionImpactDelivery.tariffFromDate) <= start &&
        new Date(consumptionImpactDelivery.tariffToDate) >= endDate
      ) {
        setProfileCost(undefined);
        const profileCostData = await fetchProfileCost(
          selectedMeteringPointContract,
          activeUser.userId,
          startOfDay(startDate)?.toISOString(),
          endOfDay(endDate)?.toISOString(),
        );
        if (profileCostData) {
          setProfileCost(profileCostData[0][customerType === 'business' ? 'profilePrice' : 'profilePriceWithVat']);
        }
      } else {
        setProfileCost(undefined);
      }
    }

    // update data
    if (
      selectedMeteringPointContract &&
      !(
        activeFilter === prevControls.activeFilter &&
        startDate?.toISOString() === prevControls.startDate?.toISOString() &&
        endDate?.toISOString() === prevControls.endDate?.toISOString() &&
        meteringPointGroup === prevControls.meteringPointGroup
      )
    ) {
      setPrevControls({ activeFilter, startDate, endDate, meteringPointGroup });

      if (type === 'group') {
        fetchGroupConsumptionData();
      }
    }

    if ((emptyData && type === 'single') || previousConsumption === 0 || lastYearConsumption === 0) {
      fetchComparison();
    }
    fetchCostData();
  }, [selectedMeteringPointContract, activeFilter, startDate, endDate, type, meteringPointGroup, emptyData]);

  React.useEffect(() => {
    // merge consumption, forecast and weather
    if (consumption) {
      const newData = generateDataPoints(
        consumption.items,
        activeFilter,
        t,
        customerType,
        contractIsNoPrice,
        meteringPointType,
        eliqForecast,
        weather,
        nordPoolData,
      );

      if (graphData !== newData) {
        dispatch(['changeDataLoading', false]);
        dispatch(['changeGraphData', newData]);
        if (newData) {
          setEmptyData(false);
        }

        if (
          consumption &&
          (!latestDayFetched ||
            isAfter(new Date(consumption.items.filter((day) => day.sum > 0).pop()?.startTime || new Date()), latestDay || new Date()))
        ) {
          const latestConsumptionDay: ConsumptionItem | undefined = consumption.items.filter((day) => day.sum > 0).pop();
          if (latestConsumptionDay) {
            dispatch(['changeLatestDay', new Date(latestConsumptionDay.startTime)]);
            dispatch(['changeLatestDayFetched', true]);
          }
        }

        if (total(newData, 'consumptionEstimate') > 0) {
          dispatch(['changeConsumptionEstimate', total(newData, 'consumption') + total(newData, 'consumptionEstimate')]);
        } else if (consumptionEstimate > 0 && total(newData, 'consumptionEstimate') === 0) {
          dispatch(['changeConsumptionEstimate', 0]);
        }
      }
    }
  }, [consumption, eliqForecast, weather, nordPoolData, emptyData]);

  // fetch comparison consumption data from one year before selected time range
  React.useEffect(() => {
    async function getComparisonData(): Promise<void> {
      if (graphData && graphData.length > 0 && startDate && endDate && selectedMeteringPoint) {
        dispatch(['changeDataLoading', true]);

        let viewPeriod = 'Year';
        if (activeFilter === 'week') {
          viewPeriod = 'Month';
        }

        let comparisonStart = startOfDay(subYears(startDate, 1));

        if (basicContract && isBefore(comparisonStart, new Date(basicContract.contractStartDate))) {
          dispatch(['changeShowComparisonData', false]);
          comparisonStart = new Date(basicContract.contractStartDate);
        }
        const consumptionRates = await fetchConsumptionRange(
          selectedMeteringPoint?.customerId,
          activeUser.userId,
          selectedMeteringPoint.meteringPointId,
          viewPeriod,
          startOfDay(comparisonStart)?.toISOString(),
          endOfDay(subYears(endDate, 1))?.toISOString(),
        );
        if (consumptionRates) {
          dispatch(['changeConsumption', consumptionRates]);

          const dataPoints = generateDataPoints(consumptionRates.items, activeFilter, t, customerType, contractIsNoPrice, meteringPointType);

          // merge newly fetched comparison data to existing array of consumption data and weather data
          const mergedData = graphData.map((item) => {
            const comparisonDataPoint = dataPoints.find((cItem) => {
              if (activeFilter === 'week') {
                return cItem.name === item.name;
              }

              return isSameDay(addYears(new Date(cItem.time || new Date()), 1), new Date(item.time || new Date()));
            });

            const editedDataPoint = { ...item, comparisonName: comparisonDataPoint?.name, comparisonConsumption: comparisonDataPoint?.consumption };

            if (activeFilter === 'month') {
              return { ...editedDataPoint, name: editedDataPoint.name?.split(' ')[0] };
            }

            return editedDataPoint;
          });
          dispatch(['changeDataLoading', false]);
          dispatch(['changeGraphData', mergedData]);
        }
      }
    }

    if (showComparisonData && graphData && graphData.length > 0 && !graphData[0].comparisonConsumption) {
      getComparisonData();
    }
  }, [showComparisonData, activeFilter, selectedMeteringPoint, startDate, endDate, activeUser, t]);

  async function downloadCSV(resolution?: string): Promise<void> {
    let csv = '';
    const separator = ';';
    let hourly = false;
    if (resolution === 'hourly') {
      hourly = true;
    }
    const dateFormat = activeFilter === 'day' || hourly ? 'yyyy-MM-dd HH:mm' : 'yyyy-MM-dd';

    let hourlyData: ReportingGraphDatapoint[] | undefined = undefined;

    // fetch hourly consumption data of selected time range, not limited by graph columns or fmi api limits
    if (hourly && startDate && endDate && selectedMeteringPoint) {
      setDownloadingHourlyCsv(true);

      const consumptionRates = await fetchConsumptionRange(
        selectedMeteringPoint.customerId,
        activeUser.userId,
        selectedMeteringPoint.meteringPointId,
        dayConsumptionAs15min ? 'Hour' : 'Day',
        startOfDay(startDate).toISOString(),
        endOfDay(endDate).toISOString(),
      );

      if (consumptionRates) {
        hourlyData = generateDataPoints(consumptionRates.items, 'hour', t, customerType, contractIsNoPrice, meteringPointType);
      }
      setDownloadingHourlyCsv(false);
    }

    const csvHeader = [
      t('consumption:consumption.csv-timestamp'),
      t(`consumption:consumption.csv-${productionContract ? 'production' : 'consumption'}`),
    ];

    if (customerType === 'private' && !productionContract && !contractIsNoPrice) {
      csvHeader.push(t('consumption:consumption.csv-costWithVat'));
    }
    if ((!contractIsNoPrice && customerType === 'business') || productionContract) {
      csvHeader.push(t('consumption:consumption.csv-costWithoutVat'));
    }

    // add csv headers for temperature and comparison if selected
    if (!hourly) {
      if (showComparisonData) {
        csvHeader.push(t('consumption:consumption.csv-comparison'));
      }
      csvHeader.push(t('consumption:consumption.csv-temperature'));
      csvHeader.push(t('consumption:consumption.csv-spot'));
    }

    if ((graphData || hourlyData) && startDate && endDate && selectedMeteringPoint) {
      if (graphData && !hourly) {
        const csvString = [
          csvHeader,
          ...graphData.map((item) => {
            const csvBody = [
              `"${format(new Date(item.time || new Date()), dateFormat)}"`,
              `"${item.consumption ? numberToString(item.consumption) : 0}"`,
            ];

            if (!contractIsNoPrice && customerType === 'private' && !productionContract) {
              csvBody.push(`"${item.costWithVat ? setPriceFormat(numberToString(item.costWithVat)) : 0}"`);
            }
            if (!contractIsNoPrice && (customerType === 'business' || productionContract)) {
              csvBody.push(`"${item.costWithoutVat ? setPriceFormat(numberToString(item.costWithoutVat)) : 0}"`);
            }

            // add csv data for temperature and comparison if selected
            if (!hourly) {
              if (showComparisonData) {
                csvBody.push(`"${item.comparisonConsumption ? numberToString(item.comparisonConsumption) : ''}"`);
              }
              csvBody.push(`"${item.temperature ? setPriceFormat(numberToString(item.temperature)) : ''}"`);
              csvBody.push(`"${item.spotPrice ? setPriceFormat(numberToString(item.spotPrice)) : 0}"`);
            }

            return csvBody;
          }),
        ]
          .map((e) => e.join(separator))
          .join('\n');

        csv += csvString;
      }
      if (hourlyData) {
        const csvString = [
          csvHeader,
          ...hourlyData.map((item) => {
            const csvBody = [
              `"${format(new Date(item.time || new Date()), dateFormat)}"`,
              `"${item.consumption ? numberToString(item.consumption) : 0}"`,
            ];

            if (customerType === 'private' && !productionContract && !contractIsNoPrice) {
              csvBody.push(`"${item.costWithVat ? setPriceFormat(numberToString(item.costWithVat)) : 0}"`);
            }
            if (!contractIsNoPrice && (customerType === 'business' || productionContract)) {
              csvBody.push(`"${item.costWithoutVat ? setPriceFormat(numberToString(item.costWithoutVat)) : 0}"`);
            }

            return csvBody;
          }),
        ]
          .map((e) => e.join(separator))
          .join('\n');

        csv += csvString;
      }

      const file = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=UTF-8' });
      const fileUrl = URL.createObjectURL(file);

      const invisibleCsvLink = document.createElement('a');
      document.body.appendChild(invisibleCsvLink);
      invisibleCsvLink.href = fileUrl;
      invisibleCsvLink.download = `${selectedMeteringPoint.meteringPointId}_${format(startDate, 'yyyy-MM-dd')}_${format(endDate, 'yyyy-MM-dd')}_${
        hourly ? 'hour' : activeFilter
      }.csv`;
      invisibleCsvLink.click();
      setShowDownloadModal(false);
    }
  }

  async function downloadMeteringPointGroupConsumptionReport(meteringPointGroupId: string): Promise<void> {
    let viewPeriod = 'Month';
    if (activeFilter === 'year') {
      viewPeriod = 'Year';
    }
    if (activeFilter === 'day') {
      viewPeriod = 'Day';
    }

    let newReportRequest: number | undefined = undefined;

    if (startDate && endDate) {
      setDownloadingMeteringPointGroupConsumptionReport(true);

      newReportRequest = await createMeteringPointGroupConsumptionReport(
        activeUser.userId,
        meteringPointGroupId,
        startOfDay(startDate).toISOString(),
        endOfDay(endDate).toISOString(),
        viewPeriod,
      );

      if (newReportRequest) {
        setDownloadingMeteringPointGroupConsumptionReport(false);
        setShowDownloadModal(false);
        if (props.refresh) {
          props.refresh('reports');
        }
      }
    }
  }

  const totalValue = graphData ? total(graphData, 'consumption') : 0;
  const avgValue = graphData
    ? roundNumber(
        avg(
          graphData,
          !contractIsNoPrice && consumptionAsMoney
            ? customerType === 'business' || productionContract
              ? 'costWithoutVat'
              : 'costWithVat'
            : 'consumptionWithEstimates',
        ),
      )
    : undefined;

  React.useEffect(() => {
    if (totalConsumption !== totalValue) {
      dispatch(['changeTotalConsumption', totalValue]);
    }
  }, [totalValue, totalConsumption]);

  const windowWidth = useWindowSize()[0];

  let barWidth = 15;

  if (activeFilter === 'month' && graphData) {
    if (graphData.length < 20) {
      barWidth = 30;
    }
    if (graphData.length < 10) {
      barWidth = 50;
    }
  }

  if (activeFilter === 'year') {
    barWidth = 35;
  }
  if (activeFilter === 'week') {
    barWidth = 60;
  }
  if (activeFilter === 'day') {
    barWidth = dayConsumptionAs15min ? 3 : 20;
  }

  const meteringPointOptions = generateMeteringPointOptions(basicData ? basicData.filter((b) => parseInt(b.contractTypeNo) === 0) : []);

  const lineOptions: MeteringPointOption[] = [
    {
      value: `temperature`,
      label: t(`consumption:consumption.reportingFilter-temperature`),
    },
    {
      value: `spot`,
      label: t(`consumption:consumption.reportingFilter-spot`),
    },
  ];

  const graphTypes: MeteringPointOption[] = [
    {
      value: 'bar',
      label: t(`consumption:graphs.graphTypeBar`),
    },
    {
      value: 'area',
      label: t(`consumption:graphs.graphTypeArea`),
    },
    {
      value: 'table',
      label: t(`consumption:graphs.graphTypeTable`),
    },
  ];

  return (
    <form className="reporting">
      <div className={`graph-container reporting-graph ${styles.graph}`}>
        {basicContract && <ReportingHeader />}

        {profileCost && (
          <div className={styles.profileCost}>
            {t(`consumption:consumption.profileCostTimeSpan`)} {setPriceFormat(roundNumber(profileCost))} {t(`consumption:nordPoolGraph.spotUnit`)}
          </div>
        )}

        {dataLoading && <Loading />}
        {graphData && graphData.length > 0 && !dataLoading && (
          <div className={styles.subHeader}>
            <div className={styles.total}>
              <div>
                <div>{setPriceFormat(roundNumber(totalConsumption))} kWh</div>
                {!contractIsNoPrice && (
                  <div>
                    <span>
                      {setPriceFormat(
                        roundNumber(total(graphData, customerType === 'business' || productionContract ? 'costWithoutVat' : 'costWithVat')),
                      )}
                    </span>{' '}
                    €
                  </div>
                )}
              </div>
            </div>

            <div className={styles.switches}>
              {!contractIsNoPrice && (
                <Switch
                  offLabel={t('consumption:consumption.reporting-energy')}
                  onLabel={t('consumption:consumption.reporting-money')}
                  context="consumption"
                  dispatchAction="changeConsumptionAsMoney"
                  active={consumptionAsMoney}
                />
              )}

              <Select options={graphTypes} activeOption={graphType} setActiveOption={setGraphType} />

              {activeFilter === 'day' && (
                <Switch
                  offLabel={t('consumption:consumption.reporting-day-hour')}
                  onLabel={t('consumption:consumption.reporting-day-15min')}
                  context="consumption"
                  dispatchAction="changeDayConsumptionAs15min"
                  active={dayConsumptionAs15min}
                />
              )}
            </div>
          </div>
        )}
        {basicContract && startDateInPast && <p>{t('consumption:consumption.startDateInPast')}</p>}
        <div className="graph-wrapper">
          {!dataLoading && graphData && graphData.length > 0 && (
            <>
              {graphType !== 'table' && (
                <ResponsiveContainer width={'100%'} height={400}>
                  <ComposedChart data={graphData} margin={{ top: 25, right: 0, bottom: 0, left: 0 }}>
                    <defs>
                      <linearGradient id="colorConsumption" x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" stopColor="#7FB722" stopOpacity={1} />
                        <stop offset="100%" stopColor="#7FB722" stopOpacity={0.1} />
                      </linearGradient>
                      <linearGradient id="colorConsumptionEstimate" x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" stopColor="#C0E300" stopOpacity={1} />
                        <stop offset="100%" stopColor="#C0E300" stopOpacity={0.1} />
                      </linearGradient>
                    </defs>
                    {showComparisonData && (
                      <Legend
                        verticalAlign="top"
                        iconType="square"
                        // payload={[
                        //   {
                        //     id: 'consumptionData',
                        //     type: 'square',
                        //     value: t('consumption:consumption.legendSelectedTime'),
                        //     color: '#7fb722',
                        //   },
                        //   {
                        //     id: 'comparisonData',
                        //     type: 'square',
                        //     value: t('consumption:consumption.legendComparison'),
                        //     color: '#96008f',
                        //   },
                        // ]}
                      />
                    )}
                    <CartesianGrid vertical={false} stroke="#e9e9e9" />
                    <XAxis
                      dataKey="name"
                      stroke="#000000"
                      interval={activeFilter === 'day' && dayConsumptionAs15min ? (windowWidth >= 768 ? 3 : 7) : 'preserveStartEnd'}
                    />
                    <YAxis
                      width={40}
                      yAxisId={0}
                      tick={{ fill: '#666666' }}
                      scale="linear"
                      interval="preserveStartEnd"
                      strokeWidth={1}
                      stroke="#c5c5c5"
                      tickFormatter={(value) => roundNumber(value, false, 1)}
                    >
                      <Label
                        value={!contractIsNoPrice && consumptionAsMoney ? '€' : 'kWh'}
                        offset={0}
                        position="insideTopLeft"
                        fill="#000000"
                        dx={20}
                        dy={-25}
                      />
                    </YAxis>
                    {activeOptionLine === 'temperature' && weather && (weather.data.length > 0 || weather.forecast.length > 0) && (
                      <YAxis yAxisId={1} orientation="right" unit=" °C" strokeWidth={0} tickFormatter={(value) => roundNumber(value, false, 1)} />
                    )}
                    {activeOptionLine === 'spot' && nordPoolData && nordPoolData.length > 0 && (
                      <YAxis
                        width={40}
                        yAxisId={2}
                        orientation="right"
                        label={{ value: t(`consumption:nordPoolGraph.spotUnit`), angle: 90, position: 'insideRight' }}
                        strokeWidth={0}
                        tickFormatter={(value) => roundNumber(value, false, 1)}
                      />
                    )}
                    <Tooltip content={<CustomTooltip />} cursor={false} wrapperStyle={{ width: 300 }} />
                    {graphType === 'bar' && (
                      <>
                        <Bar
                          dataKey={
                            !contractIsNoPrice && consumptionAsMoney
                              ? customerType === 'business' || productionContract
                                ? 'costWithoutVat'
                                : 'costWithVat'
                              : 'consumption'
                          }
                          stackId="a"
                          barSize={barWidth}
                          radius={[50, 50, 0, 0]}
                          style={{ cursor: 'pointer' }}
                          onClick={handleBarClick}
                        >
                          {graphData.map((entry, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill="#66921B"
                              stroke="#66921B"
                              strokeWidth={2}
                              // @ts-ignore
                              radius={
                                !consumptionAsMoney && entry.consumption && entry.consumptionEstimate && entry.consumptionEstimate > 0
                                  ? 0
                                  : [50, 50, 0, 0]
                              }
                            />
                          ))}
                        </Bar>
                        {!consumptionAsMoney && (
                          <Bar
                            dataKey="consumptionEstimate"
                            stackId="a"
                            barSize={barWidth}
                            radius={[50, 50, 0, 0]}
                            fill="#C0E300"
                            stroke="#66921B"
                            strokeWidth={2}
                            strokeDasharray="8 4"
                          />
                        )}
                        {showComparisonData && <Bar dataKey="comparisonConsumption" barSize={barWidth} fill="#96008f" />}
                      </>
                    )}
                    {graphType === 'area' && (
                      <>
                        <Area
                          type="monotone"
                          dataKey={
                            !contractIsNoPrice && consumptionAsMoney
                              ? customerType === 'business' || productionContract
                                ? 'costWithoutVat'
                                : 'costWithVat'
                              : 'consumptionAreaChart'
                          }
                          fill="url(#colorConsumption)"
                          stroke="#66921B"
                          strokeWidth={2}
                        />
                        {!consumptionAsMoney && (
                          <Area
                            type="monotone"
                            dataKey="consumptionEstimateAreaChart"
                            fill="url(#colorConsumptionEstimate)"
                            stroke="#66921B"
                            strokeWidth={2}
                            strokeDasharray="8 4"
                          />
                        )}
                      </>
                    )}
                    {type === 'single' && activeOptionLine === 'temperature' && weather && weather.data.length > 0 && (
                      <Line yAxisId={1} type="monotone" dot={false} dataKey="temperature" stroke="#004107" strokeWidth={3} />
                    )}
                    {type === 'single' && activeOptionLine === 'temperature' && weather && weather.forecast.length > 0 && (
                      <Line
                        yAxisId={1}
                        type="monotone"
                        dot={false}
                        dataKey="temperatureForecast"
                        stroke="#004107"
                        strokeDasharray="12 9"
                        strokeWidth={3}
                      />
                    )}
                    {type === 'single' && activeOptionLine === 'spot' && nordPoolData && nordPoolData.length > 0 && (
                      <Line yAxisId={2} type="monotone" dot={false} dataKey="spotPrice" stroke="#004107" strokeWidth={3} />
                    )}
                    {avgValue && (
                      <ReferenceLine
                        y={avgValue}
                        label={
                          <ReferenceLabel
                            value={`${t('consumption:consumption.avg')}: ${setPriceFormat(avgValue)} ${
                              !contractIsNoPrice && consumptionAsMoney ? '€' : 'kWh'
                            }`}
                          />
                        }
                        stroke="#666B6D"
                        strokeWidth={1}
                        strokeDasharray="3 3"
                      />
                    )}
                  </ComposedChart>
                </ResponsiveContainer>
              )}
              {graphType === 'table' && <ReportingTable productionContract={productionContract} />}
            </>
          )}

          {!dataLoading && (!graphData || (graphData && graphData.length === 0)) && <p>{t(`consumption:consumption.noConsumptionDataTime`)}</p>}
        </div>
        <footer className={styles.footer}>
          {type === 'single' && activeOptionMP && (
            <>
              <div className={styles.selectWrapper}>
                <Select
                  options={meteringPointOptions}
                  activeOption={activeOptionMP}
                  setActiveOption={setActiveOptionMP}
                  idToggle
                  search={meteringPointOptions.length > 5 ? t(`consumption:consumption.searchMeteringPoint`) : undefined}
                />
              </div>
              {graphData && graphData.length > 0 && graphType !== 'table' && (
                <div className={styles.selectWrapper}>
                  <Select options={lineOptions} activeOption={activeOptionLine} setActiveOption={setActiveOptionLine} />
                </div>
              )}
            </>
          )}

          {graphData && graphData.length > 0 && (
            <div className={styles.download} ref={downloadRef}>
              <Button
                className="download"
                onClick={(e): void => {
                  e.preventDefault();
                  setShowDownloadModal(!showDownloadModal);
                }}
              >
                <>{t('consumption:consumption.download')} CSV</>
              </Button>
              {showDownloadModal && (
                <div className={styles.downloadModal}>
                  <div>
                    <button
                      onClick={(e): void => {
                        e.preventDefault();
                        gaEventIfAllowed({
                          category: 'Lataa CSV',
                          action: 'Lataa CSV',
                        });
                        downloadCSV();
                      }}
                    >
                      {t('consumption:consumption.download')} (CSV)
                    </button>
                  </div>
                  <div>
                    <button
                      onClick={(e): void => {
                        e.preventDefault();
                        gaEventIfAllowed({
                          category: 'Lataa tunteina',
                          action: 'Lataa tunteina',
                        });
                        downloadCSV('hourly');
                      }}
                    >
                      {t('consumption:consumption.downloadHourly')} (CSV)
                    </button>
                  </div>
                  {type === 'group' && meteringPointGroup && (
                    <div>
                      <button
                        onClick={(e): void => {
                          e.preventDefault();
                          gaEventIfAllowed({
                            category: 'Lataa kattava Excel-raportti',
                            action: 'Lataa kattava Excel-raportti',
                          });
                          downloadMeteringPointGroupConsumptionReport(meteringPointGroup);
                        }}
                      >
                        {t('consumption:meteringPointGroup.downloadGroupConsumptionReport')}
                      </button>
                    </div>
                  )}
                  <div>{(downloadingHourlyCsv || downloadingMeteringPointGroupConsumptionReport) && <Loading size="small" />}</div>
                </div>
              )}
            </div>
          )}
        </footer>
        {graphData && graphData.length > 0 && <p>{t('consumption:consumption.disclaimer')}</p>}
      </div>
    </form>
  );
};

export default ReportingGraph;
